import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, Info } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  appVersionApi,
  AppPlatform,
  AppVersionPolicy,
  UpdateAppVersionPayload,
  validatePolicy,
} from '../../api/appVersion';

interface AppVersionPageProps {
  isDark: boolean;
}

const PLATFORM_LABEL: Record<AppPlatform, string> = { ios: 'iOS (App Store)', android: 'Android (Google Play)' };

function toForm(p: AppVersionPolicy): UpdateAppVersionPayload {
  return {
    latest_version: p.latest_version,
    min_version: p.min_version,
    release_notes_uz: p.release_notes_uz,
    release_notes_ru: p.release_notes_ru,
    reminder_interval_days: p.reminder_interval_days,
    store_url: p.store_url,
  };
}

function errorMessage(error: any, fallback: string): string {
  const msg = error?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join('; ');
  return msg || fallback;
}

function PolicyCard({
  policy,
  isDark,
  onSaved,
}: {
  policy: AppVersionPolicy;
  isDark: boolean;
  onSaved: (p: AppVersionPolicy) => void;
}) {
  const [form, setForm] = useState<UpdateAppVersionPayload>(() => toForm(policy));
  const [saving, setSaving] = useState(false);

  useEffect(() => setForm(toForm(policy)), [policy]);

  const errors = useMemo(() => validatePolicy(form), [form]);
  const hasErrors = Object.keys(errors).length > 0;
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(policy));

  const set = <K extends keyof UpdateAppVersionPayload>(key: K, value: UpdateAppVersionPayload[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (hasErrors) return;
    setSaving(true);
    try {
      const updated = await appVersionApi.update(policy.platform, {
        ...form,
        latest_version: form.latest_version.trim(),
        min_version: form.min_version.trim(),
        store_url: form.store_url.trim(),
      });
      onSaved(updated);
      toast.success(`${PLATFORM_LABEL[policy.platform]} saqlandi`);
    } catch (error: any) {
      toast.error(errorMessage(error, 'Saqlashda xatolik'));
    } finally {
      setSaving(false);
    }
  };

  const labelCls = cn('text-sm font-medium', isDark ? 'text-slate-300' : 'text-slate-700');
  const inputCls = (bad?: string) =>
    cn(
      isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200',
      bad && 'border-red-500 focus-visible:ring-red-500',
    );
  const hintCls = cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500');
  const Err = ({ k }: { k: keyof UpdateAppVersionPayload }) =>
    errors[k] ? <p className="text-xs text-red-500">{errors[k]}</p> : null;

  return (
    <Card
      className={cn(
        'rounded-2xl border-0 p-6 space-y-5',
        isDark ? 'bg-[#1E293B] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className={cn('text-lg font-semibold', isDark ? 'text-white' : 'text-[#0F172A]')}>
            {PLATFORM_LABEL[policy.platform]}
          </h2>
          <p className={hintCls}>
            Oxirgi o‘zgarish: {policy.updated_at ? new Date(policy.updated_at).toLocaleString() : '—'}
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving || hasErrors || !dirty}
          className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Saqlash
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className={labelCls}>Oxirgi versiya</Label>
          <Input
            value={form.latest_version}
            onChange={(e) => set('latest_version', e.target.value)}
            placeholder="3.4.0"
            className={inputCls(errors.latest_version)}
          />
          <Err k="latest_version" />
          <p className={hintCls}>Pastroq versiyalarga “Yangi versiya chiqdi” oynasi ko‘rsatiladi.</p>
        </div>
        <div className="space-y-1.5">
          <Label className={labelCls}>Minimal versiya</Label>
          <Input
            value={form.min_version}
            onChange={(e) => set('min_version', e.target.value)}
            placeholder="3.0.0"
            className={inputCls(errors.min_version)}
          />
          <Err k="min_version" />
          <p className={hintCls}>Bundan past versiyalar majburiy yangilanadi (ilova bloklanadi).</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className={labelCls}>Nima yangi (o‘zbekcha)</Label>
          <Textarea
            rows={5}
            value={form.release_notes_uz}
            onChange={(e) => set('release_notes_uz', e.target.value)}
            placeholder="• Yangi test rejimi&#10;• Xatolar tuzatildi"
            className={inputCls()}
          />
        </div>
        <div className="space-y-1.5">
          <Label className={labelCls}>Что нового (русский)</Label>
          <Textarea
            rows={5}
            value={form.release_notes_ru}
            onChange={(e) => set('release_notes_ru', e.target.value)}
            placeholder="• Новый режим тестов&#10;• Исправлены ошибки"
            className={inputCls()}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-4">
        <div className="space-y-1.5">
          <Label className={labelCls}>Eslatma oralig‘i (kun)</Label>
          <Input
            type="number"
            min={1}
            max={30}
            value={Number.isFinite(form.reminder_interval_days) ? form.reminder_interval_days : ''}
            onChange={(e) => set('reminder_interval_days', e.target.value === '' ? NaN : Number(e.target.value))}
            className={inputCls(errors.reminder_interval_days)}
          />
          <Err k="reminder_interval_days" />
        </div>
        <div className="space-y-1.5">
          <Label className={labelCls}>Store havolasi</Label>
          <Input
            value={form.store_url}
            onChange={(e) => set('store_url', e.target.value)}
            className={inputCls(errors.store_url)}
          />
          <Err k="store_url" />
        </div>
      </div>
    </Card>
  );
}

export default function AppVersionPage({ isDark }: AppVersionPageProps) {
  const [policies, setPolicies] = useState<AppVersionPolicy[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      setPolicies(await appVersionApi.list());
    } catch (error: any) {
      toast.error(errorMessage(error, 'Versiyalarni yuklab bo‘lmadi'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaved = (updated: AppVersionPolicy) =>
    setPolicies((prev) => prev.map((p) => (p.platform === updated.platform ? updated : p)));

  return (
    <div className="space-y-6">
      <div>
        <h1 className={cn('text-2xl lg:text-3xl font-bold tracking-tight', isDark ? 'text-white' : 'text-[#0F172A]')}>
          Ilova versiyalari
        </h1>
        <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
          Mobil ilovani yangilash qoidalari — iOS va Android uchun alohida.
        </p>
      </div>

      <div
        className={cn(
          'flex gap-3 rounded-xl p-4 text-sm',
          isDark ? 'bg-amber-500/10 text-amber-200' : 'bg-amber-50 text-amber-900',
        )}
      >
        <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p>
            <b>Oxirgi versiya</b> — shu store’da allaqachon chiqqan eng yangi versiya. Undan past versiyadagi
            foydalanuvchilarga yopiladigan “Yangi versiya chiqdi” oynasi chiqadi (“Keyinroq”dan keyin — eslatma
            oralig‘i o‘tgach yana). Test yechish paytida ko‘rsatilmaydi.
          </p>
          <p>
            <b>Minimal versiya</b> — bundan past versiyalar ilovadan foydalana olmaydi (internetsiz ham). Uni{' '}
            <b>faqat kerakli versiya shu store’da haqiqatan chiqqanidan keyin</b> ko‘taring, aks holda
            foydalanuvchilar yangilay olmay qoladi.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-10">
          <Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" />
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {policies.map((p) => (
            <PolicyCard key={p.platform} policy={p} isDark={isDark} onSaved={handleSaved} />
          ))}
        </div>
      )}
    </div>
  );
}
